import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FaSpinner } from 'react-icons/fa';

/**
 * Button — the canonical app button (Phase 3 merge of the legacy `Button` and
 * `TwButton`): Tailwind-class mechanism (purgeable, token-themed) with the
 * TwButton visual spec — fixed heights, 1px border on every variant, 8px
 * radius, weight 600. `TwButton` re-exports this with a `sm` default size.
 */
const VARIANT_CLASSES = {
  primary:
    'tw-bg-primary-600 tw-text-white tw-border-primary-600 hover:tw-bg-primary-700 hover:tw-border-primary-700',
  secondary:
    'tw-bg-white tw-text-slate-700 tw-border-slate-200 hover:tw-bg-slate-50',
  ghost:
    'tw-bg-transparent tw-text-slate-700 tw-border-transparent hover:tw-bg-slate-100',
  ghostOnDark:
    'tw-bg-transparent tw-text-white tw-border-transparent hover:tw-bg-slate-800',
  danger:
    'tw-bg-red-600 tw-text-white tw-border-red-600 hover:tw-bg-red-700 hover:tw-border-red-700',
  info:
    'tw-bg-primary-600 tw-text-white tw-border-primary-600 hover:tw-bg-primary-700 hover:tw-border-primary-700',
  success:
    'tw-bg-green-600 tw-text-white tw-border-green-600 hover:tw-bg-green-700 hover:tw-border-green-700',
  soft:
    'tw-bg-slate-100 tw-text-slate-700 tw-border-slate-100 hover:tw-bg-slate-200 hover:tw-border-slate-200',
  warning:
    'tw-bg-yellow-400 tw-text-slate-900 tw-border-yellow-400 hover:tw-bg-yellow-500 hover:tw-border-yellow-500',
  // Soft amber "tip/help" accent — stands apart from plain buttons without the
  // alarm of a solid warning yellow (used for Guide/Quick-help affordances).
  guide:
    'tw-bg-amber-100 tw-text-amber-700 tw-border-amber-300 hover:tw-bg-amber-200'
};

const SIZE_CLASSES = {
  xs: 'tw-h-6 tw-px-2 tw-text-[11px] tw-gap-1',
  sm: 'tw-h-7 tw-px-2.5 tw-text-xs tw-gap-1.5',
  md: 'tw-h-9 tw-px-3.5 tw-text-[13px] tw-gap-2',
  lg: 'tw-h-11 tw-px-5 tw-text-sm tw-gap-2'
};

const ICON_SIZES = { xs: 10, sm: 11, md: 13, lg: 14 };

const Button = React.forwardRef(
  (
    {
      children,
      type = 'button',
      variant = 'primary',
      size = 'md',
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      disabled = false,
      loading = false,
      fullWidth = false,
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    const iconSize = ICON_SIZES[size] || ICON_SIZES.md;

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        className={classNames(
          'tw-inline-flex tw-items-center tw-justify-center tw-font-semibold',
          'tw-rounded-lg tw-border tw-border-solid tw-whitespace-nowrap tw-select-none',
          'tw-transition-colors tw-duration-100',
          'disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
          VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary,
          SIZE_CLASSES[size] || SIZE_CLASSES.md,
          fullWidth && 'tw-w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <FaSpinner className="tw-animate-spin" size={iconSize} />
        ) : (
          LeftIcon && <LeftIcon size={iconSize} />
        )}
        {children}
        {RightIcon && !loading && <RightIcon size={iconSize} />}
      </button>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf(Object.keys(VARIANT_CLASSES)),
  size: PropTypes.oneOf(Object.keys(SIZE_CLASSES)),
  leftIcon: PropTypes.elementType,
  rightIcon: PropTypes.elementType,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func
};

export default Button;
